import React, { createContext, useContext, useEffect, useState, Suspense } from 'react';
import { Loader2 } from 'lucide-react';
import { supabase, type Session, type AuthUser, sessionManager } from '@/lib/supabase/client';
import { QueryCacheProvider } from '@/contexts/QueryCacheContext';
import { ReactQueryProvider } from '@/lib/react-query/provider';
import { UserProvider } from '@/components/providers/UserProvider';
import { NavigationPanelContextProvider } from '@/components/navigation/NavigationPanelContext';

// Supabase context type
interface SupabaseContextType {
  client: typeof supabase;
  session: Session | null;
  user: AuthUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  signOut: () => Promise<{ error: Error | null }>;
  refreshSession: () => Promise<{ session: Session | null; error: Error | null }>;
}

// Create Supabase context
const SupabaseContext = createContext<SupabaseContextType | null>(null);

export interface SimpleAuthProviderProps {
  children: React.ReactNode;
}

export function SimpleAuthProvider({ children }: SimpleAuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const supabaseContextValue: SupabaseContextType = {
    client: supabase,
    session,
    user,
    loading,
    isAuthenticated: !!session && !!user,
    signOut: sessionManager.signOut,
    refreshSession: sessionManager.refreshSession,
  };

  return (
    <Suspense fallback={<div className="flex items-center justify-center h-screen"><Loader2 className="h-8 w-8 animate-spin" /></div>}>
      <SupabaseContext.Provider value={supabaseContextValue}>
        <ReactQueryProvider>
          <QueryCacheProvider>
            <UserProvider>
              <NavigationPanelContextProvider>
                {loading ? (
                  <div className="fixed inset-0 bg-background flex items-center justify-center">
                    <div className="flex flex-col items-center gap-4">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      <span className="text-sm text-muted-foreground">
                        Loading...
                      </span>
                    </div>
                  </div>
                ) : (
                  children
                )}
              </NavigationPanelContextProvider>
            </UserProvider>
          </QueryCacheProvider>
        </ReactQueryProvider>
      </SupabaseContext.Provider>
    </Suspense>
  );
}

// Export Supabase context hook
export const useSupabase = () => {
  const context = useContext(SupabaseContext);
  if (!context) {
    throw new Error('useSupabase must be used within a SimpleAuthProvider');
  }
  return context;
};

// Export supabase client for components that need direct access (compatibility)
export const supabaseClient = supabase;

// Default export for compatibility
export default SimpleAuthProvider;